import React, { useEffect, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import styles from "./DownloadAppModal.module.scss";
import {
  X,
  Download,
  FileText,
  Clock,
  Loader2,
  AlertCircle,
  RotateCcw,
} from "lucide-react";
import { toast } from "top-brains-ui";

interface DownloadAppModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DownloadAppModal: React.FC<DownloadAppModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [windowsVersion, setWindowsVersion] = useState<string | null>(null);
  const [windowsFileName, setWindowsFileName] = useState<string | null>(null);
  const [linuxVersion, setLinuxVersion] = useState<string | null>(null);
  const [linuxFileName, setLinuxFileName] = useState<string | null>(null);
  const [isLoadingVersion, setIsLoadingVersion] = useState<boolean>(true);
  const [windowsError, setWindowsError] = useState<boolean>(false);
  const [linuxError, setLinuxError] = useState<boolean>(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const appEnv = import.meta.env.VITE_APP_ENV || "test";
  const env = ["test", "preprod", "prod"].includes(appEnv) ? appEnv : "test";

  const latestYmlUrl =
    import.meta.env.VITE_ELECTRON_LATEST_YML_URL ||
    `https://qspiderelp.s3.ap-south-1.amazonaws.com/Electron/${env}/latest.yml`;

  const latestLinuxYmlUrl =
    import.meta.env.VITE_ELECTRON_LATEST_LINUX_YML_URL ||
    (import.meta.env.VITE_ELECTRON_LATEST_YML_URL
      ? import.meta.env.VITE_ELECTRON_LATEST_YML_URL.replace(
          /latest\.yml(?:\?.*)?$/i,
          "latest-linux.yml",
        )
      : `https://qspiderelp.s3.ap-south-1.amazonaws.com/Electron/${env}/latest-linux.yml`);

  const downloadBaseUrl =
    import.meta.env.VITE_ELECTRON_DOWNLOAD_BASE_URL ||
    `https://qspiderelp.s3.ap-south-1.amazonaws.com/Electron/${env}/`;

  const parseYaml = (yamlContent: string) => {
    // Match version field (e.g., version: 1.0.2 or version: '1.0.2')
    const versionMatch = yamlContent.match(
      /^version:\s*['"]?([^'"\r\n]+)['"]?/m,
    );
    // Match path field if present (e.g., path: CALIBRATE-TEST Setup 1.0.2.exe or CALIBRATE-TEST-1.0.2.AppImage)
    const pathMatch = yamlContent.match(/^path:\s*['"]?([^'"\r\n]+)['"]?/m);
    // Match url field under files if present
    const urlMatch = yamlContent.match(
      /^\s*-\s*url:\s*['"]?([^'"\r\n]+)['"]?/m,
    );

    const version =
      versionMatch && versionMatch[1] ? versionMatch[1].trim() : null;
    const rawFile = (pathMatch && pathMatch[1]) || (urlMatch && urlMatch[1]);
    const file = rawFile ? rawFile.trim() : null;

    return { version, file };
  };

  const fetchLatestVersion = useCallback(async () => {
    setIsLoadingVersion(true);
    setWindowsError(false);
    setLinuxError(false);

    const cacheBuster = `?t=${Date.now()}`;
    const winUrl = latestYmlUrl.includes("?")
      ? `${latestYmlUrl}&t=${Date.now()}`
      : `${latestYmlUrl}${cacheBuster}`;
    const linUrl = latestLinuxYmlUrl.includes("?")
      ? `${latestLinuxYmlUrl}&t=${Date.now()}`
      : `${latestLinuxYmlUrl}${cacheBuster}`;

    const [winResult, linResult] = await Promise.allSettled([
      fetch(winUrl),
      fetch(linUrl),
    ]);

    // Parse Windows (latest.yml)
    if (winResult.status === "fulfilled" && winResult.value.ok) {
      try {
        const text = await winResult.value.text();
        const { version, file } = parseYaml(text);
        if (version) {
          setWindowsVersion(version);
          setWindowsFileName(file);
          setWindowsError(false);
        } else {
          setWindowsError(true);
        }
      } catch {
        setWindowsError(true);
      }
    } else {
      setWindowsError(true);
    }

    // Parse Linux (latest-linux.yml)
    if (linResult.status === "fulfilled" && linResult.value.ok) {
      try {
        const text = await linResult.value.text();
        const { version, file } = parseYaml(text);
        if (version) {
          setLinuxVersion(version);
          setLinuxFileName(file);
          setLinuxError(false);
        } else {
          setLinuxError(true);
        }
      } catch {
        setLinuxError(true);
      }
    } else {
      setLinuxError(true);
    }

    setIsLoadingVersion(false);
  }, [latestYmlUrl, latestLinuxYmlUrl]);

  useEffect(() => {
    if (isOpen) {
      fetchLatestVersion();
    }
  }, [isOpen, fetchLatestVersion]);

  if (!isOpen) return null;

  const formattedBaseUrl = downloadBaseUrl.endsWith("/")
    ? downloadBaseUrl
    : `${downloadBaseUrl}/`;

  // Build target executable file name strictly from latest.yml
  const targetWindowsFileName = windowsFileName
    ? windowsFileName.replace(/\s+/g, "+")
    : windowsVersion
      ? `CALIBRATE-${env.toUpperCase()}+Setup+${windowsVersion}.exe`
      : null;

  const windowsExeUrl = targetWindowsFileName
    ? `${formattedBaseUrl}${targetWindowsFileName}`
    : null;

  // Build target executable file name strictly from latest-linux.yml
  const targetLinuxFileName = linuxFileName
    ? linuxFileName.replace(/\s+/g, "+")
    : linuxVersion
      ? `CALIBRATE-${env.toUpperCase()}+Setup+${linuxVersion}.AppImage`
      : null;

  const linuxAppImageUrl = targetLinuxFileName
    ? `${formattedBaseUrl}${targetLinuxFileName}`
    : null;

  const manualUrl = "/Calibrate_Installation_Manual.html";

  const handleDownloadWindows = () => {
    if (isLoadingVersion) {
      toast.info(
        "Checking version",
        "Please wait while we verify the latest version...",
      );
      return;
    }

    if (!windowsVersion || !windowsExeUrl) {
      toast.error(
        "Download unavailable",
        "Unable to fetch the latest Windows application version from server. Please retry.",
      );
      return;
    }

    try {
      const downloadDisplayName =
        windowsFileName ||
        `CALIBRATE-${env.toUpperCase()}-Setup-${windowsVersion}.exe`;

      const link = document.createElement("a");
      link.href = windowsExeUrl;
      link.target = "_parent";
      link.setAttribute("download", downloadDisplayName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(
        "Download started",
        `Downloading Calibrate v${windowsVersion} for Windows`,
      );
    } catch {
      window.open(windowsExeUrl, "_blank");
    }
  };

  const handleDownloadLinux = () => {
    if (isLoadingVersion) {
      toast.info(
        "Checking version",
        "Please wait while we verify the latest version...",
      );
      return;
    }

    if (!linuxVersion || !linuxAppImageUrl) {
      toast.error(
        "Download unavailable",
        "Unable to fetch the latest Linux application version from server. Please retry.",
      );
      return;
    }

    try {
      const downloadDisplayName =
        linuxFileName ||
        `CALIBRATE-${env.toUpperCase()}-Setup-${linuxVersion}.AppImage`;

      const link = document.createElement("a");
      link.href = linuxAppImageUrl;
      link.target = "_parent";
      link.setAttribute("download", downloadDisplayName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(
        "Download started",
        `Downloading Calibrate v${linuxVersion} for Linux`,
      );
    } catch {
      window.open(linuxAppImageUrl, "_blank");
    }
  };

  const handleOpenManual = () => {
    window.open(manualUrl, "_blank");
  };

  const displayWindowsVersion = isLoadingVersion
    ? "Checking..."
    : windowsVersion;

  const displayLinuxVersion = isLoadingVersion
    ? "Checking..."
    : linuxVersion;

  const platforms = [
    {
      id: "windows",
      name: "Windows",
      version: displayWindowsVersion,
      status: "ready" as const,
      extension: ".exe",
      icon: (
        <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
          <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.902-1.799" />
        </svg>
      ),
      color: "#0078D7",
      bgColor: "rgba(0, 120, 215, 0.08)",
      onClick: handleDownloadWindows,
      isLoading: isLoadingVersion,
      isError: !isLoadingVersion && (windowsError || !windowsVersion),
    },
    {
      id: "mac",
      name: "macOS",
      version: null,
      status: "in_progress" as const,
      extension: ".dmg",
      icon: (
        <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.61-.75 1.04-1.8 0.92-2.85-.9.04-2 .6-2.65 1.35-.56.64-1.06 1.7-0.93 2.73 1.01.08 2.05-.48 2.66-1.23z" />
        </svg>
      ),
      color: "#64748b",
      bgColor: "rgba(100, 116, 139, 0.08)",
      onClick: undefined,
      isLoading: false,
      isError: false,
    },
    {
      id: "linux",
      name: "Linux",
      version: displayLinuxVersion,
      status: "ready" as const,
      extension: ".AppImage",
      icon: (
        <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
          <path d="M12.002 0c-2.484 0-4.5 2.016-4.5 4.5 0 .54.108 1.05.281 1.527C6.01 6.55 4.5 8.358 4.5 10.5c0 1.258.532 2.395 1.385 3.203C5.347 14.593 5 15.753 5 17c0 3.866 3.134 7 7.002 7s7.002-3.134 7.002-7c0-1.247-.347-2.407-.885-3.297.853-.808 1.385-1.945 1.385-3.203 0-2.142-1.51-3.95-3.283-4.473.173-.477.281-.987.281-1.527 0-2.484-2.016-4.5-4.5-4.5zm0 2.25c1.242 0 2.25 1.008 2.25 2.25 0 .34-.08.66-.223.945a5.485 5.485 0 0 0-4.054 0A2.24 2.24 0 0 1 9.752 4.5c0-1.242 1.008-2.25 2.25-2.25zM9.75 8.25a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zm4.5 0a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zM12 11.25c1.657 0 3 .895 3 2s-1.343 2-3 2-3-.895-3-2 1.343-2 3-2zm0 6.75c2.62 0 4.75 1.903 4.75 4.25H7.25c0-2.347 2.13-4.25 4.75-4.25z" />
        </svg>
      ),
      color: "#EA580C",
      bgColor: "rgba(234, 88, 12, 0.08)",
      onClick: handleDownloadLinux,
      isLoading: isLoadingVersion,
      isError: !isLoadingVersion && (linuxError || !linuxVersion),
    },
  ];

  return ReactDOM.createPortal(
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={styles.modalCard}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Close Button */}
        <button
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Close modal"
        >
          <X size={18} />
        </button>

        {/* Modal Header */}
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Download Calibrate Desktop App</h2>
          <p className={styles.modalSubtitle}>
            Select your operating system to download the secure test
            environment.
          </p>
        </div>

        {/* Error Banner when version files fail to load */}
        {!isLoadingVersion &&
          (windowsError || !windowsVersion) &&
          (linuxError || !linuxVersion) && (
            <div className={styles.errorBanner}>
              <AlertCircle size={16} />
              <span>
                Failed to load the latest application version details from server.
              </span>
            </div>
          )}

        {/* 3 OS Cards Grid */}
        <div className={styles.cardsGrid}>
          {platforms.map((platform) => {
            const isReady = platform.status === "ready";

            return (
              <div
                key={platform.id}
                className={`${styles.platformCard} ${
                  isReady ? styles.activeCard : styles.disabledCard
                }`}
              >
                <div className={styles.cardHeader}>
                  <div
                    className={styles.brandIcon}
                    style={{
                      color: platform.color,
                      backgroundColor: platform.bgColor,
                    }}
                  >
                    {platform.icon}
                  </div>
                  {isReady ? (
                    <span className={styles.readyBadge}>Available</span>
                  ) : (
                    <span className={styles.progressBadge}>
                      <Clock size={11} /> Coming Soon
                    </span>
                  )}
                </div>

                <div className={styles.cardContent}>
                  <h3 className={styles.platformName}>{platform.name}</h3>
                  {platform.version ? (
                    <span className={styles.platformVersion}>
                      v{platform.version}
                    </span>
                  ) : (
                    !platform.isLoading &&
                    isReady && (
                      <span className={styles.platformVersionError}>
                        Version info unavailable
                      </span>
                    )
                  )}
                </div>

                <div className={styles.cardAction}>
                  {isReady ? (
                    platform.isLoading ? (
                      <button className={styles.downloadBtn} disabled>
                        <Loader2 size={15} className={styles.spinIcon} />
                        <span>Checking...</span>
                      </button>
                    ) : platform.isError ? (
                      <div className={styles.errorActionWrapper}>
                        <button className={styles.errorBtn} disabled>
                          <AlertCircle size={14} />
                          <span>Unavailable</span>
                        </button>
                        <button
                          className={styles.retryBtn}
                          onClick={fetchLatestVersion}
                          title="Retry fetching version"
                        >
                          <RotateCcw size={13} />
                          <span>Retry</span>
                        </button>
                      </div>
                    ) : (
                      <button
                        className={styles.downloadBtn}
                        onClick={platform.onClick}
                      >
                        <Download size={15} />
                        <span>Download {platform.extension}</span>
                      </button>
                    )
                  ) : (
                    <button className={styles.disabledBtn} disabled>
                      <span>Coming Soon</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer info & manual guide */}
        <div className={styles.modalFooter}>
          <div className={styles.footerHelp}>
            <FileText size={15} color="#434db8" />
            <span>Need help with installation?</span>
            <button className={styles.manualLinkBtn} onClick={handleOpenManual}>
              View Manual
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DownloadAppModal;
